/**
 * AVRenderer — draws one audio-visualizer frame from the drivers of
 * AudioReactiveCore. Deterministic: a frame is a function of
 * (scene, time, drivers, events) and of the frames before it, so the same
 * code renders the live page, the offline video export and the bench
 * (tests/playwright/audio-visualizer-reactivity-bench.mjs).
 *
 *   var r = AVRenderer.create(canvas, { scenes: AVScenes, onLost, onRestored });
 *   r.setScene('campfire');            // crossfades from the current one (1 s)
 *   r.setPostFx(true);
 *   r.render({ time: s, dt: s, d: drivers, events: [{t, g, s}], now: s, motion: 1 });
 *   r.resize();                         // after the canvas box changes
 *
 * What the renderer adds on top of the drivers (all dt-based):
 *   u_glow, u_bassS, u_midS, u_highS — the level and the groups smoothed
 *             (70 ms up, 300 ms down): what large areas may brighten with — a
 *             full-frame swing on every hat is a strobe;
 *   u_flash — a full-frame flash on a strong kick / snare / beat, at most one
 *             per 0.45 s (WCAG 2.3.1 allows three a second), damped by u_motion;
 *   spectrum texture with a peak hold (row 1) for the Spectrum scene;
 *   u_events — the last 8 hits with a stable pseudo-random position each.
 *
 * Shaders compile lazily: the current scene first, neighbours in idle time
 * (KHR_parallel_shader_compile when present) — compiling all 15 up front froze
 * the first frame for ~0.7 s on Windows (inp_slow, startVisualizer, 2026-09).
 *
 * ES5. window.AVRenderer.
 */
(function (root) {
    'use strict';

    var DPR_CAP = 1.5;
    /* WCAG allows three flashes a second; keep a margin under it (the Storm
       with bloom measured 3 at 0.34 s) */
    var FLASH_MIN_GAP_S = 0.45;
    var UNIFORMS = ['u_time', 'u_resolution', 'u_alpha', 'u_flow', 'u_flowBass', 'u_flowHigh',
        'u_level', 'u_glow', 'u_bass', 'u_mid', 'u_high', 'u_kick', 'u_snare', 'u_hat',
        'u_beat', 'u_beatPhase', 'u_beatLock', 'u_presence', 'u_intensity', 'u_bright',
        'u_flash', 'u_motion', 'u_spectrum', 'u_events', 'u_eventKind', 'u_bassS', 'u_midS', 'u_highS'];

    function Renderer(canvas, opts) {
        this.canvas = canvas;
        this.opts = opts || {};
        this.S = this.opts.scenes || root.AVScenes;
        this.byId = {};
        for (var i = 0; i < this.S.list.length; i++) this.byId[this.S.list[i].id] = this.S.list[i];
        this.scene = this.S.list[0].id;
        this.next = null;
        this.trans = 0;
        this.postFx = true;
        this.dprCap = this.opts.dprCap || DPR_CAP;
        this.fixedSize = this.opts.size || null;   /* {w, h} for offline renders */
        /* derived state */
        this.glow = 0;
        this.smooth = { bass: 0, mid: 0, high: 0 };
        this.flash = 0;
        this.lastFlashT = -1e9;
        this.peaks = new Float32Array(32);
        this.specBytes = new Uint8Array(64);
        this.ev = [];               /* {x, y, t, s, g} newest last, at most 8 */
        this.evBuf = new Float32Array(32);
        this.evKind = new Float32Array(8);
        this.seq = 0;
        this.lost = false;
        var self = this;
        canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); self.lost = true; if (self.opts.onLost) self.opts.onLost(); }, false);
        canvas.addEventListener('webglcontextrestored', function () { self.lost = false; self._init(); if (self.opts.onRestored) self.opts.onRestored(); }, false);
        this.ok = this._init();
    }

    Renderer.prototype._init = function () {
        var gl = this.canvas.getContext('webgl', { antialias: false, alpha: false, preserveDrawingBuffer: !!this.opts.preserve, powerPreference: 'high-performance' }) ||
                 this.canvas.getContext('experimental-webgl', { antialias: false, alpha: false });
        if (!gl) return false;
        this.gl = gl;
        this.par = gl.getExtension('KHR_parallel_shader_compile');
        this.programs = {};
        this.post = null;
        this.fbo = { w: 0, h: 0 };
        var buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        this.specTex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this.specTex);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, 32, 2, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, this.specBytes);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        this.resize();
        this._program(this.scene);
        return true;
    };

    /* ---- programs ---- */

    Renderer.prototype._compile = function (vs, fs) {
        var gl = this.gl;
        var v = gl.createShader(gl.VERTEX_SHADER); gl.shaderSource(v, vs); gl.compileShader(v);
        var f = gl.createShader(gl.FRAGMENT_SHADER); gl.shaderSource(f, fs); gl.compileShader(f);
        var p = gl.createProgram(); gl.attachShader(p, v); gl.attachShader(p, f);
        gl.bindAttribLocation(p, 0, 'a_pos');
        gl.linkProgram(p);
        return { p: p, v: v, f: f, ready: false, failed: false };
    };

    Renderer.prototype._check = function (rec) {
        if (rec.ready || rec.failed) return rec.ready;
        var gl = this.gl;
        if (this.par && !gl.getProgramParameter(rec.p, this.par.COMPLETION_STATUS_KHR)) return false;
        if (!gl.getProgramParameter(rec.p, gl.LINK_STATUS)) {
            rec.failed = true;
            var log = gl.getShaderInfoLog(rec.f) || gl.getProgramInfoLog(rec.p) || '';
            if (this.opts.onError) this.opts.onError(new Error('shader: ' + String(log).slice(0, 300)));
            return false;
        }
        rec.u = {};
        for (var i = 0; i < UNIFORMS.length; i++) {
            var n = UNIFORMS[i], loc = gl.getUniformLocation(rec.p, n);
            if (!loc && (n === 'u_events' || n === 'u_eventKind')) loc = gl.getUniformLocation(rec.p, n + '[0]');
            if (loc) rec.u[n] = loc;
        }
        rec.ready = true;
        return true;
    };

    Renderer.prototype._program = function (id) {
        if (!this.gl || !this.byId[id]) return null;
        var rec = this.programs[id];
        if (!rec) rec = this.programs[id] = this._compile(this.S.vert, this.S.lib + this.byId[id].frag);
        return this._check(rec) ? rec : null;
    };

    /* Compile the scenes next to `id` while nobody is waiting for them. */
    Renderer.prototype.warm = function (ids) {
        for (var i = 0; i < ids.length; i++) if (this.gl && this.byId[ids[i]] && !this.programs[ids[i]]) {
            this.programs[ids[i]] = this._compile(this.S.vert, this.S.lib + this.byId[ids[i]].frag);
        }
    };

    Renderer.prototype._postPrograms = function () {
        if (this.post) return this.post.ready ? this.post : null;
        var gl = this.gl, P = this.S.post, self = this;
        var names = { blit: ['u_tex', 'u_res'], bright: ['u_tex', 'u_res', 'u_thresh'], blur: ['u_tex', 'u_res', 'u_dir'],
            composite: ['u_scene', 'u_bloom', 'u_res', 'u_time', 'u_bloom_intensity', 'u_ca_strength', 'u_grain', 'u_vignette', 'u_flash'] };
        this.post = { ready: false, recs: {} };
        Object.keys(names).forEach(function (k) { self.post.recs[k] = self._compile(self.S.vert, P[k]); self.post.recs[k].names = names[k]; });
        return null;
    };

    Renderer.prototype._postReady = function () {
        var post = this.post;
        if (!post) { this._postPrograms(); post = this.post; }
        if (post.ready) return post;
        var gl = this.gl, all = true;
        for (var k in post.recs) {
            var rec = post.recs[k];
            if (rec.ready) continue;
            if (rec.failed) return null;
            if (this.par && !gl.getProgramParameter(rec.p, this.par.COMPLETION_STATUS_KHR)) { all = false; continue; }
            if (!gl.getProgramParameter(rec.p, gl.LINK_STATUS)) { rec.failed = true; return null; }
            rec.u = {};
            for (var i = 0; i < rec.names.length; i++) rec.u[rec.names[i]] = gl.getUniformLocation(rec.p, rec.names[i]);
            rec.ready = true;
        }
        post.ready = all;
        return all ? post : null;
    };

    /* ---- geometry ---- */

    Renderer.prototype.resize = function () {
        if (!this.gl) return;
        var w, h;
        if (this.fixedSize) { w = this.fixedSize.w; h = this.fixedSize.h; }
        else {
            var dpr = Math.min(root.devicePixelRatio || 1, this.dprCap);
            var rect = this.canvas.getBoundingClientRect();
            w = Math.max(1, Math.round(rect.width * dpr));
            h = Math.max(1, Math.round(rect.height * dpr));
        }
        if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; }
    };

    Renderer.prototype._fbos = function (w, h) {
        var gl = this.gl, f = this.fbo;
        if (f.w === w && f.h === h) return f;
        ['scene', 'bright', 'blurA', 'blurB'].forEach(function (k) {
            if (f[k]) { gl.deleteTexture(f[k].tex); gl.deleteFramebuffer(f[k].fb); }
        });
        function make(width, height) {
            var tex = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            var fb = gl.createFramebuffer();
            gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
            return { tex: tex, fb: fb };
        }
        var bw = Math.max(1, w >> 1), bh = Math.max(1, h >> 1);
        f.scene = make(w, h); f.bright = make(bw, bh); f.blurA = make(bw, bh); f.blurB = make(bw, bh);
        f.w = w; f.h = h; f.bw = bw; f.bh = bh;
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return f;
    };

    /* ---- controls ---- */

    Renderer.prototype.setScene = function (id, instant) {
        if (!this.byId[id]) return;
        if (instant || !this.gl) { this.scene = id; this.next = null; this.trans = 0; this._program(id); return; }
        if (id === this.scene && !this.next) return;
        this.next = id;
        this.trans = 0;
        this._program(id);
    };
    Renderer.prototype.currentScene = function () { return this.next || this.scene; };
    /* true once the program of `id` is linked (parallel compile may still run) */
    Renderer.prototype.isReady = function (id) { return !!this._program(id || this.scene); };
    Renderer.prototype.setPostFx = function (on) { this.postFx = !!on; };
    /* a new source brings a new clock: hits of the old one must not replay */
    Renderer.prototype.clearEvents = function () { this.ev.length = 0; this.flash = 0; this.lastFlashT = -1e9; };

    /* ---- derived drivers ---- */

    Renderer.prototype._derive = function (st) {
        var d = st.d, dt = Math.max(0, Math.min(0.25, st.dt || 0)), m = st.motion == null ? 1 : st.motion;
        var lv = d.level;
        this.glow += (lv - this.glow) * (1 - Math.exp(-dt / (lv > this.glow ? 0.07 : 0.3)));
        var sm = this.smooth;
        sm.bass += (d.bass - sm.bass) * (1 - Math.exp(-dt / (d.bass > sm.bass ? 0.07 : 0.3)));
        sm.mid += (d.mid - sm.mid) * (1 - Math.exp(-dt / (d.mid > sm.mid ? 0.07 : 0.3)));
        sm.high += (d.high - sm.high) * (1 - Math.exp(-dt / (d.high > sm.high ? 0.07 : 0.3)));
        this.flash *= Math.exp(-dt / 0.12);
        var evs = st.events || [];
        for (var i = 0; i < evs.length; i++) {
            var e = evs[i];
            this.seq++;
            /* a stable pseudo-random spot per hit (golden-ratio walk) */
            var x = (this.seq * 0.6180339887) % 1, y = (this.seq * 0.7548776662 + 0.1) % 1;
            this.ev.push({ x: 0.1 + 0.8 * x, y: 0.15 + 0.7 * y, t: e.t, s: e.s, g: e.g });
            if (this.ev.length > 8) this.ev.shift();
            if ((e.g === 0 || e.g === 1 || e.g === 3) && e.s >= 0.45 && st.now - this.lastFlashT >= FLASH_MIN_GAP_S) {
                this.lastFlashT = st.now;
                this.flash = Math.max(this.flash, e.s);
            }
        }
        var sp = d.spectrum, rel = Math.exp(-dt / 0.6);
        for (var b = 0; b < 32; b++) {
            var v = sp ? sp[b] : 0;
            this.peaks[b] = Math.max(v, this.peaks[b] * rel - dt * 0.05);
            this.specBytes[b] = Math.round(Math.max(0, Math.min(1, v)) * 255);
            this.specBytes[32 + b] = Math.round(Math.max(0, Math.min(1, this.peaks[b])) * 255);
        }
        for (i = 0; i < 8; i++) {
            var ev = this.ev[this.ev.length - 1 - i];
            var o = i * 4;
            if (ev && st.now - ev.t < 3.0) {
                this.evBuf[o] = ev.x; this.evBuf[o + 1] = ev.y; this.evBuf[o + 2] = Math.max(0, st.now - ev.t); this.evBuf[o + 3] = ev.s;
                this.evKind[i] = ev.g;
            } else {
                this.evBuf[o + 3] = 0; this.evKind[i] = 0;
            }
        }
        this.motion = m;
    };

    /* ---- drawing ---- */

    Renderer.prototype._drawScene = function (rec, alpha, st, w, h) {
        var gl = this.gl, u = rec.u, d = st.d, m = this.motion;
        gl.useProgram(rec.p);
        function f1(n, v) { if (u[n]) gl.uniform1f(u[n], v); }
        f1('u_time', st.time);
        if (u.u_resolution) gl.uniform2f(u.u_resolution, w, h);
        f1('u_alpha', alpha);
        f1('u_flow', d.flow); f1('u_flowBass', d.flowBass); f1('u_flowHigh', d.flowHigh);
        f1('u_level', d.level); f1('u_glow', this.glow);
        f1('u_bassS', this.smooth.bass); f1('u_midS', this.smooth.mid); f1('u_highS', this.smooth.high);
        f1('u_bass', d.bass); f1('u_mid', d.mid); f1('u_high', d.high);
        f1('u_kick', d.kick); f1('u_snare', d.snare); f1('u_hat', d.hat);
        f1('u_beat', d.beat); f1('u_beatPhase', d.beatPhase); f1('u_beatLock', d.beatLock);
        f1('u_presence', d.presence); f1('u_intensity', d.intensity); f1('u_bright', d.bright);
        f1('u_flash', this.flash * (0.35 + 0.65 * m)); f1('u_motion', m);
        if (u.u_spectrum) {
            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, this.specTex);
            gl.uniform1i(u.u_spectrum, 1);
            gl.activeTexture(gl.TEXTURE0);
        }
        if (u.u_events) gl.uniform4fv(u.u_events, this.evBuf);
        if (u.u_eventKind) gl.uniform1fv(u.u_eventKind, this.evKind);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    Renderer.prototype.render = function (st) {
        if (!this.gl || this.lost) return false;
        var gl = this.gl;
        if (!this.fixedSize) this.resize();
        var w = this.canvas.width, h = this.canvas.height;
        this._derive(st);
        gl.bindTexture(gl.TEXTURE_2D, this.specTex);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 32, 2, gl.LUMINANCE, gl.UNSIGNED_BYTE, this.specBytes);
        var cur = this._program(this.scene);
        var nxt = this.next ? this._program(this.next) : null;
        if (this.next && nxt) {
            this.trans = Math.min(1, this.trans + Math.max(0, st.dt || 0));
            if (this.trans >= 1) { this.scene = this.next; this.next = null; cur = nxt; nxt = null; }
        }
        var post = this.postFx ? this._postReady() : null;
        var f = post ? this._fbos(w, h) : null;
        gl.bindFramebuffer(gl.FRAMEBUFFER, f ? f.scene.fb : null);
        gl.viewport(0, 0, w, h);
        gl.clearColor(0, 0, 0, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);
        if (cur) {
            if (nxt) {
                gl.enable(gl.BLEND);
                gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
                this._drawScene(cur, 1 - this.trans, st, w, h);
                this._drawScene(nxt, this.trans, st, w, h);
                gl.disable(gl.BLEND);
            } else {
                this._drawScene(cur, 1, st, w, h);
            }
        }
        if (f) this._composite(post, f, st, w, h);
        return !!cur;
    };

    Renderer.prototype._composite = function (post, f, st, w, h) {
        var gl = this.gl, R = post.recs, m = this.motion;
        function pass(rec, target, vw, vh, tex) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, target);
            gl.viewport(0, 0, vw, vh);
            gl.useProgram(rec.p);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, tex);
        }
        pass(R.bright, f.bright.fb, f.bw, f.bh, f.scene.tex);
        gl.uniform1i(R.bright.u.u_tex, 0); gl.uniform2f(R.bright.u.u_res, f.bw, f.bh); gl.uniform1f(R.bright.u.u_thresh, 0.55);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        var src = f.bright.tex;
        for (var i = 0; i < 2; i++) {
            pass(R.blur, f.blurA.fb, f.bw, f.bh, src);
            gl.uniform1i(R.blur.u.u_tex, 0); gl.uniform2f(R.blur.u.u_res, f.bw, f.bh); gl.uniform2f(R.blur.u.u_dir, 1 / f.bw, 0);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
            pass(R.blur, f.blurB.fb, f.bw, f.bh, f.blurA.tex);
            gl.uniform2f(R.blur.u.u_dir, 0, 1 / f.bh);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
            src = f.blurB.tex;
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, w, h);
        var C = R.composite;
        gl.useProgram(C.p);
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, f.scene.tex); gl.uniform1i(C.u.u_scene, 0);
        gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, f.blurB.tex); gl.uniform1i(C.u.u_bloom, 1);
        gl.uniform2f(C.u.u_res, w, h);
        gl.uniform1f(C.u.u_time, st.time);
        gl.uniform1f(C.u.u_bloom_intensity, 0.55 * (0.5 + 0.5 * m));
        gl.uniform1f(C.u.u_ca_strength, (0.5 + st.d.high * 1.2) * (0.5 + 0.5 * m));
        gl.uniform1f(C.u.u_grain, 0.04 * (0.5 + 0.5 * m));
        gl.uniform1f(C.u.u_vignette, 0.55);
        gl.uniform1f(C.u.u_flash, this.flash * (0.35 + 0.65 * m));
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.activeTexture(gl.TEXTURE0);
    };

    Renderer.prototype.destroy = function () {
        var ext = this.gl && this.gl.getExtension('WEBGL_lose_context');
        if (ext) try { ext.loseContext(); } catch (e) {}
        this.gl = null;
    };

    root.AVRenderer = {
        create: function (canvas, opts) { return new Renderer(canvas, opts); },
        UNIFORMS: UNIFORMS
    };
})(typeof window !== 'undefined' ? window : this);
